import { fetchCartMutation } from './cart-quantity-limits.js';

/**
 * Product Bundle Manager 
 */
class ProductBundleManager {
  constructor() {
    /** @type {HTMLElement|null} */
    this.bundleSection = null;
    /** @type {HTMLButtonElement|null} */
    this.bundleButton = null;
    /** @type {HTMLInputElement[]} */
    this.checkboxes = [];
    this.originalButtonText = 'Add Items To Cart';
    
    this.init();
  }

  init() {
    this.bundleSection = document.querySelector('.product-list-bundle .section-resource-list');
    
    if (!this.bundleSection) {
      console.warn('Bundle section not found');
      return;
    }

    this.findBundleButton();
    this.findCheckboxes();
    this.convertButtonToElement();
    this.addEventListeners();
    this.updateButtonText();
  }

  findBundleButton() {
    const contentArea = this.bundleSection?.querySelector('.section-resource-list__content');
    if (contentArea) {
      this.bundleButton = contentArea.querySelector('a[class*="button"], button[class*="button"]');
    }
  }

  findCheckboxes() {
    if (!this.bundleSection) return;
    this.checkboxes = Array.from(this.bundleSection.querySelectorAll('.bundle-checkbox'));
  }

  convertButtonToElement() {
    if (!this.bundleButton) {
      console.warn('Bundle button not found');
      return;
    }

    if (this.bundleButton.tagName.toLowerCase() === 'a') {
      const newButton = document.createElement('button');
      
      Array.from(this.bundleButton.attributes).forEach(attr => {
        if (attr.name !== 'href' && attr.name !== 'role' && attr.name !== 'aria-disabled') {
          newButton.setAttribute(attr.name, attr.value);
        }
      });
      
      newButton.setAttribute('type', 'button');
      newButton.setAttribute('role', 'button');
      
      newButton.innerHTML = this.bundleButton.innerHTML;
      newButton.textContent = this.originalButtonText;
      
      if (this.bundleButton.parentNode) {
        this.bundleButton.parentNode.replaceChild(newButton, this.bundleButton);
        this.bundleButton = newButton;
      }
    }
    
    if (this.bundleButton) {
      this.bundleButton.textContent = this.originalButtonText;
    }
  }

  addEventListeners() {
    this.checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        this.updateButtonText();
      });
    });

    if (this.bundleButton) {
      this.bundleButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.addSelectedItemsToCart();
      });
    }
  }

  getSelectedProducts() {
    return this.checkboxes
      .filter(checkbox => checkbox.checked && !checkbox.disabled)
      .map(checkbox => ({
        productId: checkbox.dataset.productId || '',
        handle: checkbox.dataset.productHandle || '',
        variantId: checkbox.dataset.variantId || '',
        checkbox: checkbox
      }));
  }

  async getProductPrices() {
    const selectedProducts = this.getSelectedProducts();
    const prices = [];
    
    for (const product of selectedProducts) {
      try {
        const priceAttr = product.checkbox.dataset.productPrice;
        if (priceAttr) {
          const price = parseFloat(priceAttr);
          
          if (!isNaN(price) && price > 0) {
            prices.push(price);
            continue;
          }
        }
        
        const productCard = product.checkbox.closest('.resource-list__item');
        const priceElement = productCard?.querySelector('.price__current, .price .money');
        
        if (priceElement) {
          const priceText = priceElement.textContent?.trim() || '';
          const priceMatch = priceText.match(/[\d,]+\.?\d*/);
          
          if (priceMatch) {
            const domPrice = parseFloat(priceMatch[0].replace(',', ''));
            prices.push(domPrice);
            continue;
          }
        }
        
        const response = await fetch(`/products/${product.handle}.js`);
        if (response.ok) {
          const productData = await response.json();
          const variant = productData.variants.find((/** @type {any} */ v) => v.id == product.variantId);
          
          if (variant) {
            prices.push(variant.price / 100);
          } else {
            prices.push(0);
          }
        } else {
          prices.push(0);
        }
        
      } catch (error) {
        console.error('Error fetching product price:', error);
        prices.push(0);
      }
    }
    
    return prices;
  }

  async updateButtonText() {
    if (!this.bundleButton) return;
    
    const selectedProducts = this.getSelectedProducts();
    
    if (selectedProducts.length === 0) {
      this.bundleButton.textContent = this.originalButtonText;
      this.bundleButton.disabled = true;
      return;
    }

    this.bundleButton.disabled = false;
    
    try {
      const prices = await this.getProductPrices();
      const totalPrice = prices.reduce((sum, price) => sum + price, 0);
      
      const formattedPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (/** @type {any} */ (window.Shopify))?.currency?.active || 'USD'
      }).format(totalPrice);
      
      this.bundleButton.textContent = `${this.originalButtonText} - ${formattedPrice}`;
    } catch (error) {
      console.error('Error updating button text:', error);
      this.bundleButton.textContent = `${this.originalButtonText} (${selectedProducts.length} items)`;
    }
  }

  async addSelectedItemsToCart() {
    if (!this.bundleButton) return;
    
    const selectedProducts = this.getSelectedProducts();
    
    if (selectedProducts.length === 0) {
      alert('Please select at least one product');
      return;
    }

    const originalText = this.bundleButton.textContent;
    this.bundleButton.textContent = 'Adding to cart...';
    this.bundleButton.disabled = true;

    try {
      const items = selectedProducts.map(product => ({
        id: product.variantId,
        quantity: 1
      }));

      const response = await fetchCartMutation('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      }, 'add');

      if (response.ok) {
        const cartData = await response.json();
        
        const cartResponse = await fetch('/cart.js');
        const currentCart = await cartResponse.json();
        
        const cartSections = await this.getCartSections();
        
        const cartUpdateEvent = new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: currentCart,
            sourceId: 'product-bundle',
            data: {
              itemCount: currentCart.item_count,
              source: 'product-bundle',
              sections: cartSections
            }
          }
        });
        
        document.dispatchEvent(cartUpdateEvent);
        
        setTimeout(() => {
          const cartDrawer = document.querySelector('cart-drawer-component');
          if (cartDrawer && typeof (/** @type {any} */ (cartDrawer)).open === 'function') {
            (/** @type {any} */ (cartDrawer)).open();
          }
        }, 300);
        
        this.updateButtonText();
        
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add items to cart');
      }
      
    } catch (error) {
      console.error('Error adding items to cart:', error);
      alert(error.message);
    } finally {
      this.bundleButton.disabled = false;
      if (this.getSelectedProducts().length === 0) {
        this.bundleButton.textContent = this.originalButtonText;
      } else if (originalText) {
        this.bundleButton.textContent = originalText;
      }
    }
  }

  /**
    * Fetches updated cart sections including cart-drawer and cart-icon-bubble
   * @returns {Promise<Object>}
   */
  async getCartSections() {
    try {
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      /** @type {string[]} */
      const sectionIds = [];
      
      cartItemsComponents.forEach((component) => {
        if (component instanceof HTMLElement && component.dataset.sectionId) {
          sectionIds.push(component.dataset.sectionId);
        }
      });
      
      const sectionsToRequest = [...new Set([...sectionIds, 'cart-drawer', 'cart-icon-bubble'])];
      
      const response = await fetch(`/?sections=${sectionsToRequest.join(',')}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data;
      }
      
      return {};
      
    } catch (error) {
      console.error('Error fetching cart sections:', error);
      return {};
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductBundleManager();
  });
} else {
  new ProductBundleManager();
}

/** @type {any} */ (window).ProductBundleManager = ProductBundleManager;